/**
 * Form validation helpers shared by all forms.
 *
 * Hostname rule (single source of truth - previously NodeModal and
 * OriginGroupModal used two different regexes):
 * - RFC 1123 labels: 1-63 chars of [A-Za-z0-9-], not starting/ending with "-"
 * - labels separated by single dots, total length <= 253, no trailing dot
 * - the last label must not be all-numeric (RFC 3696 s.2), so dotted
 *   numbers such as "999.1.1.1" are rejected as hostnames instead of being
 *   mistaken for one; real addresses go through the IP validators
 *
 * Where an origin (upstream server) is expected, use `isValidOriginHost`,
 * which accepts a hostname OR an IPv4 address. IPv6 literals are not accepted
 * there because the generated nginx `server <address>:<port>` line would need
 * brackets, which the control plane template does not add.
 */

const HOSTNAME_LABEL = /^[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?$/;
const IPV4_OCTET = /^(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)$/;
const IPV6_GROUP = /^[0-9a-fA-F]{1,4}$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Alphabetic TLD ("com") or an IDN TLD in punycode ("xn--p1ai" for .рф)
const TLD_PATTERN = /^(?:[a-zA-Z]{2,63}|xn--[a-zA-Z0-9-]{1,59})$/;

/**
 * Basic e-mail shape check (something@something.tld, no whitespace)
 */
export function isValidEmail(email: string): boolean {
  return EMAIL_PATTERN.test(email.trim());
}

/**
 * Strict dotted-quad IPv4 (0-255 per octet, no leading zeros)
 */
export function isValidIPv4(value: string): boolean {
  const octets = value.split('.');
  return octets.length === 4 && octets.every((octet) => IPV4_OCTET.test(octet));
}

/**
 * IPv6 in full or compressed ("::") form, optionally with an embedded IPv4 tail
 */
export function isValidIPv6(value: string): boolean {
  if (!value.includes(':')) return false;

  const doubleColonCount = value.split('::').length - 1;
  if (doubleColonCount > 1) return false;

  let groups = value.split(':');
  let groupBudget = 8;

  // Embedded IPv4 (e.g. ::ffff:192.168.0.1) occupies two groups
  const last = groups[groups.length - 1];
  if (last.includes('.')) {
    if (!isValidIPv4(last)) return false;
    groups = groups.slice(0, -1);
    groupBudget = 6;
  }

  if (doubleColonCount === 1) {
    const [head, tail] = value.split('::');
    const headGroups = head ? head.split(':') : [];
    const tailGroups = tail ? tail.split(':') : [];
    if (last.includes('.')) tailGroups.pop();
    const explicit = [...headGroups, ...tailGroups];
    return explicit.length < groupBudget && explicit.every((group) => IPV6_GROUP.test(group));
  }

  return groups.length === groupBudget && groups.every((group) => IPV6_GROUP.test(group));
}

/**
 * IPv4 or IPv6 address
 */
export function isValidIpAddress(value: string): boolean {
  return isValidIPv4(value) || isValidIPv6(value);
}

/**
 * The eight 16-bit groups of a syntactically valid IPv6 address (any notation:
 * compressed, expanded, upper case, embedded IPv4 tail), or null.
 */
function ipv6Groups(value: string): number[] | null {
  if (!isValidIPv6(value)) return null;

  // An embedded IPv4 tail (a.b.c.d) is two groups
  let text = value;
  const tailStart = text.lastIndexOf(':') + 1;
  const tail = text.slice(tailStart);
  if (tail.includes('.')) {
    const [a, b, c, d] = tail.split('.').map(Number);
    text = `${text.slice(0, tailStart)}${((a << 8) | b).toString(16)}:${((c << 8) | d).toString(16)}`;
  }

  const [head, rest] = text.split('::');
  const headGroups = head ? head.split(':') : [];
  const restGroups = rest ? rest.split(':') : [];
  const zeros = rest === undefined ? [] : Array<string>(8 - headGroups.length - restGroups.length).fill('0');
  return [...headGroups, ...zeros, ...restGroups].map((group) => parseInt(group, 16));
}

/**
 * Public IPv6 address of a node, published in DNS AAAA records: a global
 * unicast IPv6 address in any notation (mirrors the server rule). IPv4 and
 * IPv4-mapped addresses, the unspecified (::) and loopback (::1) addresses,
 * link-local (fe80::/10), multicast (ff00::/8) and unique local (fc00::/7)
 * addresses, prefixes and zone IDs ("fe80::1%eth0") are rejected.
 */
export function isValidIpv6Address(value: string): boolean {
  const groups = ipv6Groups(value);
  if (!groups) return false;

  const zerosBefore = (index: number) => groups.slice(0, index).every((group) => group === 0);
  const first = groups[0];
  return !(
    (zerosBefore(7) && groups[7] <= 1) || // :: and ::1
    (zerosBefore(5) && groups[5] === 0xffff) || // IPv4-mapped
    (first & 0xffc0) === 0xfe80 || // link-local
    (first & 0xff00) === 0xff00 || // multicast
    (first & 0xfe00) === 0xfc00 // unique local
  );
}

/**
 * RFC 1123 hostname (see module comment). Single-label names such as
 * "localhost" are allowed.
 */
export function isValidHostname(hostname: string): boolean {
  if (!hostname || hostname.length > 253) return false;

  const labels = hostname.split('.');
  if (!labels.every((label) => HOSTNAME_LABEL.test(label))) return false;

  // The last label must not be all-numeric (that would be an IP-like string)
  return !/^\d+$/.test(labels[labels.length - 1]);
}

/**
 * Host accepted for an origin / upstream server: hostname or IPv4
 */
export function isValidOriginHost(value: string): boolean {
  return isValidIPv4(value) || isValidHostname(value);
}

/**
 * Fully qualified domain name with an alphabetic TLD, e.g. "example.com".
 * With `allowWildcard`, a single leading "*." label is accepted
 * ("*.example.com").
 */
export function isValidDomainName(name: string, options: { allowWildcard?: boolean } = {}): boolean {
  let candidate = name;
  if (options.allowWildcard && candidate.startsWith('*.')) {
    candidate = candidate.slice(2);
  }

  if (!candidate.includes('.') || !isValidHostname(candidate)) return false;

  const labels = candidate.split('.');
  return TLD_PATTERN.test(labels[labels.length - 1]);
}

/**
 * TCP port 1-65535 (accepts numbers or numeric strings)
 */
export function isValidPort(value: number | string): boolean {
  const port = typeof value === 'number' ? value : Number(value.trim());
  return Number.isInteger(port) && port >= 1 && port <= 65535;
}
